// Localization audit, the checks (LOCALIZATION_AUDIT_PLAN.md, Part 1, 2026-11-03).
// Pure functions: every text source is flattened to { lang: { key: text } } and each
// translation is compared with the English. Admin-only; no AI calls.

export const AUDIT_LANGS = ["pt-BR", "es-419", "fr-CA"];
const ALL_LANGS = ["en", ...AUDIT_LANGS];

export const PROBLEMS = {
  missing: "Missing",
  empty: "Empty",
  same: "Same as English",
  placeholder: "Placeholder mismatch",
  long: "May not fit",
  extra: "Not in English",
  informal: "Informal address",
};

// A translation longer than this many times the English may not fit its space.
export const LONG_RATIO = 1.6;
// Short English texts vary too much in length to compare (e.g. "OK" → "Aceptar").
const LONG_MIN_CHARS = 12;

// Texts that really are the same in every language.
const SAME_OK_RE = new RegExp(
  "^(?:[\\d\\s.,:;/%+()–—-]*|e-?mail|ok|pdf|word|cv|ia|ai|linkedin|google|beta|" +
    "english job interview simulator|interview simulator|language masters(?: software)?|" +
    // Language names are shown in their own language.
    "english|português(?: \\(brasil\\))?|español(?: \\(latinoamérica\\))?|français(?: \\(canada\\))?|" +
    "(?:a1|a2|b1|b2|c1|c2)\\+?|guided mode|advanced mode)$",
  "i",
);
const ACRONYM_RE = /^[A-Z0-9]{1,6}$/; // "JD", "PDF", "STT"
const PATTERN_RE = /^(?:[X\s-]+|n\/a|(?:[A-Z]{0,3}\$)?[\d\s.,–—-]+\+?)$/i; // "XXXX-XXXX", "N/A", "C$15–19.99", "C$40+"
// Words spelled the same as the English in that language.
const SAME_WORDS = {
  "pt-BR": /^(?:volume|marketing|feedback|email|e-mail|status)$/i,
  "es-419": /^(?:no|marketing|email)$/i,
  "fr-CA": /^(?:questions?|excellent|acceptable|version|canada|volume|marketing|composite|description|page|options?|notes?|date|message|minutes?|transcription)$/i,
};

function reallySame(text, lang) {
  const words = text.replace(/\{[^{}]+\}|\d+/g, "").replace(/[.:,;!?()]/g, "").trim();
  // A quoted phrase is example interview wording, English in every language by design.
  const quoted = /^["“].*["”]$/.test(text);
  return quoted || SAME_OK_RE.test(text) || ACRONYM_RE.test(text) || PATTERN_RE.test(text) || Boolean(SAME_WORDS[lang]?.test(words));
}

// Formal address is the rule for Spanish and French (usted / vous).
// Whole words only (accented letters count as letters, so not \b). Case matters for
// "ti" ("TI" is Tecnología de la Información); French "ton" is left out ("ton
// professionnel", the tone).
const INFORMAL_RE = {
  "es-419": /(?<!\p{L})(?:[Tt]ú|[Tt]us?|[Tt]e|ti|[Cc]ontigo|[Tt]ienes|[Pp]uedes|[Qq]uieres|[Ee]res|[Ee]stás|[Dd]ebes)(?!\p{L})/u,
  "fr-CA": /(?<!\p{L})(?:(?:[Tt]u|[Tt]oi|[Tt]a|[Tt]es)(?!\p{L})|[Tt][’'](?=\p{L}))/u,
};
// Keys that mark structure, not text ({ k: "heading", text: "…" }).
const NOT_TEXT_KEYS = new Set(["k", "id", "type", "kind", "icon", "href", "screens", "category"]);

const PLACEHOLDER_RE = /\{[^{}]+\}|\$\{[^{}]+\}|%[sd]/g;
// Legacy aliases ("pt-br", "both", "fr-ca") repeat a language and are skipped.
const LANG_KEYS = new Set([...ALL_LANGS, "pt", "es", "fr", "pt-br", "both", "fr-ca"]);

function canonicalLang(key) {
  if (key === "pt" || key === "pt-br") return "pt-BR";
  if (key === "es") return "es-419";
  if (key === "fr") return "fr-CA";
  return key;
}

function isLangMap(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.filter((k) => ALL_LANGS.includes(k)).length >= 2 && keys.every((k) => LANG_KEYS.has(k));
}

// A template function's text, with its arguments shown as {1}, {2}, …
function templateText(fn) {
  try {
    const out = fn("{1}", "{2}", "{3}", "{4}");
    return typeof out === "string" ? out : null;
  } catch {
    return null;
  }
}

function flattenInto(value, prefix, out) {
  if (typeof value === "string") {
    out[prefix || "(text)"] = value;
  } else if (typeof value === "function") {
    const text = templateText(value);
    if (text !== null) out[prefix || "(text)"] = text;
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => flattenInto(v, prefix ? `${prefix}.${i}` : String(i), out));
  } else if (value && typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      if (!NOT_TEXT_KEYS.has(k)) flattenInto(v, prefix ? `${prefix}.${k}` : k, out);
    }
  }
}

/** Any copy shape → { lang: { key: text } }: a block per language ({ en: {…}, "pt-BR": {…} })
 * or a translation per text ({ title: { en, "pt-BR", … } }), nested in any mix. */
export function flattenSource(source) {
  const out = Object.fromEntries(ALL_LANGS.map((l) => [l, {}]));
  const walk = (value, prefix) => {
    if (isLangMap(value)) {
      for (const [lang, inner] of Object.entries(value)) {
        const code = canonicalLang(lang);
        const alias = code !== lang && code in value; // "pt-br" next to "pt-BR"
        if (out[code] && !alias) flattenInto(inner, prefix, out[code]);
      }
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      for (const [k, v] of Object.entries(value)) walk(v, prefix ? `${prefix}.${k}` : k);
    }
  };
  walk(source, "");
  return out;
}

const placeholders = (text) => (String(text).match(PLACEHOLDER_RE) || []).sort().join(" ");

/** The problems with one translation of one text. */
export function checkText(english, translated, lang) {
  const found = [];
  if (translated === undefined) return english === undefined ? found : ["missing"];
  if (english === undefined) return ["extra"];
  const en = String(english).trim();
  const tr = String(translated).trim();
  if (!tr) return en ? ["empty"] : found;
  if (tr === en && en && !reallySame(en, lang)) found.push("same");
  if (placeholders(en) !== placeholders(tr)) found.push("placeholder");
  if (en.length >= LONG_MIN_CHARS && tr.length > en.length * LONG_RATIO) found.push("long");
  if (INFORMAL_RE[lang]?.test(tr)) found.push("informal");
  return found;
}

/** One row per text: { source, screen, area, key, en, "pt-BR", "es-419", "fr-CA", problems: { lang: [...] } }. */
export function auditSources(sources) {
  const rows = [];
  for (const src of sources) {
    const texts = src.texts || flattenSource(src.copy);
    const keys = new Set();
    for (const lang of ALL_LANGS) Object.keys(texts[lang] || {}).forEach((k) => keys.add(k));
    for (const key of keys) {
      const row = { source: src.id, screen: src.screen, area: src.area || "app", key, en: texts.en?.[key], problems: {} };
      for (const lang of AUDIT_LANGS) {
        row[lang] = texts[lang]?.[key];
        const found = src.noEnglish ? checkText(row[lang] ?? "", row[lang], lang).filter((p) => p !== "same") : checkText(row.en, row[lang], lang);
        if (found.length) row.problems[lang] = found;
      }
      rows.push(row);
    }
  }
  return rows;
}

/** Counts by language and problem type. */
export function summarize(rows) {
  const byLang = Object.fromEntries(AUDIT_LANGS.map((l) => [l, Object.fromEntries(Object.keys(PROBLEMS).map((p) => [p, 0]))]));
  for (const row of rows) {
    for (const [lang, found] of Object.entries(row.problems)) for (const p of found) byLang[lang][p] += 1;
  }
  return { texts: rows.length, withProblems: rows.filter((r) => Object.keys(r.problems).length).length, byLang };
}

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function toCsv(rows) {
  const head = ["Screen", "Source", "Key", "English", ...AUDIT_LANGS, "Problems"];
  const lines = rows.map((r) =>
    [r.screen, r.source, r.key, r.en, ...AUDIT_LANGS.map((l) => r[l]),
      Object.entries(r.problems).map(([l, p]) => `${l}: ${p.map((x) => PROBLEMS[x]).join(", ")}`).join("; ")]
      .map(csvCell).join(","),
  );
  return "﻿" + [head.map(csvCell).join(","), ...lines].join("\r\n");
}

export function toText(rows, { onlyProblems = false } = {}) {
  const out = [];
  let screen = null;
  for (const r of rows) {
    if (onlyProblems && !Object.keys(r.problems).length) continue;
    if (r.screen !== screen) {
      screen = r.screen;
      out.push("", "=".repeat(72), screen, "=".repeat(72));
    }
    out.push("", `[${r.key}]  (${r.source})`, `  en     : ${r.en ?? "(missing)"}`);
    for (const lang of AUDIT_LANGS) {
      const flag = r.problems[lang] ? `   <-- ${r.problems[lang].map((x) => PROBLEMS[x]).join(", ")}` : "";
      out.push(`  ${lang.padEnd(7)}: ${r[lang] ?? "(missing)"}${flag}`);
    }
  }
  return out.join("\n").trim() + "\n";
}
